import React, { useState, useEffect } from 'react';
import {
  Table, Card, Button, Upload, Modal, Form, Select,
  Space, Tag, Typography, Statistic, Row, Col, message,
  Popconfirm, Steps, Alert, Divider, Progress
} from 'antd';
import {
  UploadOutlined, FileExcelOutlined, CheckCircleOutlined,
  CloseCircleOutlined, DeleteOutlined, EyeOutlined,
  CloudUploadOutlined, InboxOutlined
} from '@ant-design/icons';
import { importService } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

const SpendImport = () => {
  const [loading, setLoading] = useState(true);
  const [imports, setImports] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const fetchImports = async () => {
    try {
      setLoading(true);
      const data = await importService.getSpendImports();
      setImports(data);
    } catch (error) {
      message.error('Failed to load imports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImports();
  }, []);

  const handleFileSelect = async (file) => {
    const allowedTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(fileExt)) {
      message.error('Please upload a CSV or Excel file');
      return false;
    }

    if (file.size > 10 * 1024 * 1024) {
      message.error('File size must be less than 10MB');
      return false;
    }

    setSelectedFile(file);

    try {
      const preview = await importService.previewSpendFile(file);
      setPreviewData(preview);

      // Set suggested mappings
      if (preview.suggested_mapping) {
        setColumnMapping(preview.suggested_mapping);
        form.setFieldsValue(preview.suggested_mapping);
      }

      setCurrentStep(1);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to preview file');
      setSelectedFile(null);
    }

    return false; // Prevent auto upload
  };

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();
      setUploading(true);

      await importService.uploadSpendFile(selectedFile, values);
      message.success('File imported successfully');

      setIsModalVisible(false);
      resetModal();
      fetchImports();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await importService.deleteSpendImport(id, true);
      message.success('Import deleted');
      fetchImports();
    } catch (error) {
      message.error('Failed to delete import');
    }
  };

  const resetModal = () => {
    setCurrentStep(0);
    setSelectedFile(null);
    setPreviewData(null);
    setColumnMapping({});
    form.resetFields();
  };

  const openModal = () => {
    resetModal();
    setIsModalVisible(true);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'orange', icon: null },
      processing: { color: 'blue', icon: null },
      completed: { color: 'green', icon: <CheckCircleOutlined /> },
      failed: { color: 'red', icon: <CloseCircleOutlined /> },
    };
    const config = statusConfig[status] || { color: 'default', icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {status?.toUpperCase()}
      </Tag>
    );
  };

  // Calculate stats
  const stats = {
    totalImports: imports.length,
    totalRecords: imports.reduce((sum, imp) => sum + (imp.successful_rows || 0), 0),
    recentImports: imports.filter(imp =>
      dayjs(imp.created_at).isAfter(dayjs().subtract(7, 'days'))
    ).length,
    failedImports: imports.filter(imp => imp.status === 'failed').length,
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (name, record) => (
        <Space>
          <FileExcelOutlined style={{ color: '#217346' }} />
          <span>{name}</span>
          <Tag>{record.file_type?.toUpperCase()}</Tag>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Records',
      key: 'records',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="success">{record.successful_rows || 0} imported</Text>
          {record.failed_rows > 0 && (
            <Text type="danger">{record.failed_rows} failed</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const total = record.total_rows || 1;
        const processed = record.processed_rows || 0;
        const percent = Math.round((processed / total) * 100);
        return (
          <Progress
            percent={percent}
            size="small"
            status={record.status === 'failed' ? 'exception' : undefined}
          />
        );
      },
    },
    {
      title: 'Imported',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Delete this import?"
            description="This will also delete all associated spend records."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const steps = [
    { title: 'Select File', description: 'Upload CSV or Excel' },
    { title: 'Map Columns', description: 'Configure mapping' },
    { title: 'Import', description: 'Process data' },
  ];

  return (
    <div>
      <Title level={2}>Spend Import</Title>
      <Text type="secondary">
        Upload and manage spend data from CSV or Excel files
      </Text>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Imports"
              value={stats.totalImports}
              prefix={<CloudUploadOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Records"
              value={stats.totalRecords}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="This Week"
              value={stats.recentImports}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Failed"
              value={stats.failedImports}
              valueStyle={{ color: stats.failedImports > 0 ? '#cf1322' : '#8c8c8c' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Imports Table */}
      <Card
        title="Import History"
        extra={
          <Button type="primary" icon={<UploadOutlined />} onClick={openModal}>
            Import Spend Data
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={imports}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <InboxOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                <p style={{ marginTop: 16, color: '#8c8c8c' }}>
                  No imports yet. Upload a CSV or Excel file to get started.
                </p>
              </div>
            ),
          }}
        />
      </Card>

      {/* Import Modal */}
      <Modal
        title="Import Spend Data"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={700}
        footer={null}
        destroyOnClose
      >
        <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

        {currentStep === 0 && (
          <Dragger
            accept=".csv,.xlsx,.xls"
            beforeUpload={handleFileSelect}
            showUploadList={false}
            style={{ padding: 40 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Supports CSV, XLSX, and XLS files up to 10MB
            </p>
          </Dragger>
        )}

        {currentStep === 1 && previewData && (
          <>
            <Alert
              message={`File: ${previewData.file_name}`}
              description={`${previewData.total_rows} rows found with ${previewData.columns.length} columns`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="vendor_name"
                    label="Vendor Name Column"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Select placeholder="Select column">
                      {previewData.columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="amount"
                    label="Amount Column"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Select placeholder="Select column">
                      {previewData.columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="date"
                    label="Date Column"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Select placeholder="Select column">
                      {previewData.columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="category"
                    label="Category Column (Optional)"
                  >
                    <Select placeholder="Select column" allowClear>
                      {previewData.columns.map(col => (
                        <Option key={col} value={col}>{col}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Divider>Preview Data</Divider>
            <Table
              dataSource={previewData.sample_data.slice(0, 5)}
              columns={previewData.columns.map(col => ({
                title: col,
                dataIndex: col,
                key: col,
                ellipsis: true,
              }))}
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              rowKey={(_, index) => index}
            />

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => { setCurrentStep(0); setSelectedFile(null); setPreviewData(null); }}>
                  Back
                </Button>
                <Button type="primary" onClick={handleUpload} loading={uploading}>
                  Import Data
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SpendImport;
